import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { STATUS_ACTIVE, User, UserCollection } from '../_models';
import { Conversation, ConversationCollection } from '../_models/conversation';
import { Message, MessageCollection } from '../_models/message';
import { Room, RoomCollection } from '../_models/room';
import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root'
})
export class ChatService {

    messageSource: BehaviorSubject<string> = new BehaviorSubject('');

    public chatCreationContacts: Array<User>;

    public rooms: Array<Room> = [];
    public roomsLists: Array<Room> = [];

    public messages: Array<{items:Message, time:Date}> = [];


    /**
     * Constructor
     */
    constructor(private apiService: ApiService) {}

    /**
     * Get a conversations
     */
    public getConversation(id: string) {
        return this.apiService.getConversation(id);
    }

    /**
     * Delete message
     */
    public deleteConversation(conversationId: string): Observable<void> {
        return this.apiService.deleteConversation(conversationId);
    }

    /**
     * Get current user conversations
     */
    public getUnreadConversations():Observable<{ items: Array<Conversation>; totalItems: number}> {
        return this.apiService.getUnreadConversations().pipe(
            map((responseData: ConversationCollection) => {
                let convos: Array<Conversation> = [];
                for (let convo of responseData['hydra:member']) {
                    let current: Conversation = convo;
                    convos.push(convo);
                }

                convos = convos.sort((a: Conversation, b: Conversation) => {
                    return (
                        new Date(b.lastMessageDate).getTime() -
                        new Date(a.lastMessageDate).getTime()
                    );
                });

                return {
                    items: convos,
                    totalItems: responseData['hydra:totalItems']
                };
            })
        );
    }

    /**
     * Get current user conversations
     */
    public getConversations(count: number, page: number = 1, partial: boolean = false):Observable<{ items: Array<Conversation>; totalItems: number}> {
        return this.apiService.getConversations(count, page, partial).pipe(
            map((responseData: ConversationCollection) => {
                let convos: Array<Conversation> = [];
                for (let convo of responseData['hydra:member']) {
                    let current: Conversation = convo;
                    convos.push(convo);
                }

                convos = convos.sort((a: Conversation, b: Conversation) => {
                    return (
                        new Date(b.lastMessageDate).getTime() -
                        new Date(a.lastMessageDate).getTime()
                    );
                });

                return {
                    items: convos,
                    totalItems: responseData['hydra:totalItems']
                };
            })
        );
    }

    /**
     * Set conversation as read
     */
    public touchConversation(id: string) {
        return this.apiService.touchConversation(id);
    }

    /**
     * Get converstation messages
     */
    public getMessages(id: string, count: number = 10, maxRank:number = 2147483647, minDate:Date = null): Observable<{ items: Array<Message>; totalItems: number }> {
        return this.apiService.getMessages(id, count, maxRank).pipe(
            map((responseData: MessageCollection) => {
                let messages: Array<Message> = [];

                for (let message of responseData['hydra:member']) {
                    if (!minDate || minDate.getTime() <= new Date(message.createdAt).getTime()) {
                        messages.push(message);
                    }
                }

                return {
                    items: messages,
                    totalItems: responseData['hydra:totalItems']
                };
            })
        );
    }

    /**
     * Send a conversation message
     */
    public sendMessage(convoId: string, text: string): Observable<Message> {
        return this.apiService.sendMessage(convoId, text);
    }

    /**
     * Send a message replay
     */
    public sendReply(convoId: string, messageId: string, text: string): Observable<Message> {
        return this.apiService.sendReply(convoId, messageId, text);
    }

    /**
     * Send room message
     */
     public sendRoomReply(roomId: string, messageId: string, text: string): Observable<Message> {
        return this.apiService.sendRoomReply(roomId, messageId, text);
    }

    /**
     * Send a message reaction
     */
    public sendReaction(messageId: string, reaction: string): Observable<Message> {
        return this.apiService.sendReaction(messageId, reaction);
    }

    /**
     * Delete a message reaction
     */
     public deleteReaction(messageId: string, reaction: string): Observable<Message> {
        return this.apiService.deleteReaction(messageId, reaction);
    }

    /**
     * Send room message
     */
    public sendRoomMessage(room: string, text: string): Observable<Message> {
        return this.apiService.sendRoomMessage(room, text);
    }

    /**
     * Send an image to conversation
     */
    public sendMediaMessage(blob:any, convoId: string): Observable<Message> {
        if (blob.type.startsWith('video/')) {
            return this.apiService.sendVideoMessage(convoId, blob);
        } else {
            return this.apiService.sendImageMessage(convoId, blob);
        }
    }

    /**
     * Send an image to room
     */
    public sendRoomMediaMessage(blob:any, roomId: string): Observable<Message> {
        if (blob.type.startsWith('video/')) {
            return this.apiService.sendRoomVideoMessage(roomId, blob);
        } else {
            return this.apiService.sendRoomImageMessage(roomId, blob);
        }
    }

    /**
     * Send a recording to conversation
     */
    public sendRecordMessage(blob:any, mime:string, duration:number, convoId: string): Observable<Message> {
        return this.apiService.sendRecordMessage(convoId, blob, mime, duration);
    }

    /**
     * Delete message
     */
    public deleteMessage(messageId: string): Observable<void> {
        return this.apiService.deleteMessage(messageId);
    }

    /**
     * Get a room
     */
    public getRoom(id: string):Observable<Room>  {
        if (this.rooms[id]) {
            this.apiService.getRoom(id).subscribe((room: Room) => {
                this.rooms[id].name = room.name;
                this.rooms[id].description = room.description;
                this.rooms[id].members = room.members.filter((user) => user.status == STATUS_ACTIVE);
                this.rooms[id].members.sort((a,b) => a.username.localeCompare(b.username));
                this.rooms[id].iconPaths = room.iconPaths;
            });
            return new Observable(subscriber => {
                subscriber.next(this.rooms[id]);
            });
        } else {
            return this.apiService.getRoom(id).pipe(
                map((responseData: Room) => {
                    this.rooms[id] = responseData;
                    this.rooms[id].members = responseData.members.filter((user) => user.status == STATUS_ACTIVE);
                    this.rooms[id].members.sort((a,b) => a.username.localeCompare(b.username));
                    return responseData;
                })
            );
        }
    }

    /**
     * Enter room
     */
    public enterRoom(id: string):Observable<void>  {
        return this.apiService.enterRoom(id);
    }

    /**
     * Get online users
     */
    public getOnlines(roomId:string):Observable<{ items: Array<User>; totalItems: number}> {
        return this.apiService.getRoomOnlines(roomId).pipe(
            map((responseData: UserCollection) => {
                let users: Array<User> = [];
                for (let user of responseData['hydra:member']) {
                    users.push(user);
                }

                return {
                    items: users,
                    totalItems: responseData['hydra:totalItems']
                };
            })
        );
    }

    /**
     * Get single emoji count object
     */
    public getSingleEmojiCountObj(message: Message) {
        if(message) {
            const uniqueEmoji = [];
            message.reactions.filter(item=>{
                const isDuplicate = uniqueEmoji.includes(item.reaction);
                if (!isDuplicate) {
                    uniqueEmoji.push(item.reaction);
                    return true;
                }
                return false;
            });
            return uniqueEmoji.length;
        }else{
            return 0;
        }
    }

    /**
     * Get current user rooms by type
     */
    public getRooms(type:string, count: number, page: number = 1, partial: boolean = false):Observable<{ items: Array<Room>; totalItems: number}> {
        return this.apiService.getRooms(type, count, page, partial).pipe(
            map((responseData: RoomCollection) => {
                let convos: Array<Room> = [];
                for (let convo of responseData['hydra:member']) {
                    let current: Room = convo;
                    convos.push(convo);
                }

                // convos = convos.sort((a: Conversation, b: Conversation) => {
                //     return (
                //         new Date(b.lastMessageDate).getTime() -
                //         new Date(a.lastMessageDate).getTime()
                //     );
                // });

                if (page == 1) {
                    this.roomsLists[type] = {
                        items: convos,
                        totalItems: responseData['hydra:totalItems']
                    }
                }

                return {
                    items: convos,
                    totalItems: responseData['hydra:totalItems']
                };
            })
        );
    }

    /**
     * Create room
     */
    public createRoom(type: string, name: string, description: string, blob: any, members: Array<string>):Observable<Room> {
        return this.apiService.createRoom(type, name, description, blob, members);
    }

    /**
     * Delete room
     */
    public deleteRoom(roomId: string): Observable<void> {
        return this.apiService.deleteRoom(roomId);
    }

    /**
     * Leave room
     */
    public leaveRoom(roomId: string): Observable<void> {
        return this.apiService.leaveRoom(roomId);
    }

    /**
     * Update room members
     */
    public updateMembers(roomId: string, members:string[]): Observable<void> {
        return this.apiService.updateMembers(roomId, members);
    }

    /**
     * Update room data
     */
    public updateRoom(roomId: string, data: any):Observable<Room> {
        return this.apiService.updateRoom(roomId, data);
    }

    /**
     * Advertise typing
     */
    advertiseTyping(conversationId:string) {
        return this.apiService.advertiseTyping(conversationId);
    }

    /**
     * Add room to favorites
     */
    public addToFavorites(room: Room): Observable<Room> {
        return this.apiService.toggleRoomFavorites(room['@id']).pipe(
            map((responseData: Room) => {
                return responseData;
            })
        );
    }

    /**
     * Remove room from favorites
     */
    public removeFromFavorites(room: Room) {
        return this.apiService.toggleRoomFavorites(room['@id']).pipe(
            map((responseData: Room) => {
                return responseData;
            })
        );
    }

    /**
     * Clear
     */
    clear() {
        this.rooms = [];
        this.roomsLists = [];
    }
}
